import React, { useEffect } from 'react';
import './index.css';
import clsx from 'classnames';

/**
 * 底部页码组件
 * @param {Object} props - 组件属性
 * @param {number} props.currentPage - 当前页码
 * @param {number} props.totalPages - 总页数
 * @param {Function} props.onChange - 页码变化回调函数
 */
const Pagination = ({ currentPage, totalPages, onChange }) => {
  useEffect(() => {
    // 使用 requestAnimationFrame + setTimeout 双重保障，确保火狐浏览器滚动可靠
    const animationFrameId = requestAnimationFrame(() => {
      // 先尝试平滑滚动
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      // 为火狐浏览器添加延迟兜底，确保滚动一定执行
      const timeoutId = setTimeout(() => {
        if (window.scrollY > 0) {
          window.scrollTo(0, 0);
        }
      }, 200);

      // 清理超时定时器
      return () => clearTimeout(timeoutId);
    });

    // 清理函数：取消未执行的动画帧
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [currentPage]);

  // 处理页码点击
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onChange(page);
    }
  };

  // 处理上一页
  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  // 处理下一页
  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // 处理首页
  const handleFirstPage = () => {
    handlePageChange(1);
  };

  // 处理尾页
  const handleLastPage = () => {
    handlePageChange(totalPages);
  };

  // 生成页码列表
  const renderPageNumbers = () => {
    const pages = [];
    const showEllipsis = totalPages > 7;

    if (!showEllipsis) {
      // 总页数少于等于 7 页，全部显示
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <div
            className={clsx('pagination-num-item', {
              'is-page-active': currentPage === i,
            })}
            key={i}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </div>,
        );
      }
    } else {
      // 总页数超过 7 页，显示省略号
      for (let i = 1; i <= totalPages; i++) {
        // 始终显示第一页和最后一页
        if (i === 1 || i === totalPages) {
          pages.push(
            <div
              className={clsx('pagination-num-item', {
                'is-page-active': currentPage === i,
              })}
              key={i}
              onClick={() => handlePageChange(i)}
            >
              {i}
            </div>,
          );
        }
        // 显示当前页附近的页码
        else if (
          (currentPage <= 4 && i <= 5) ||
          (currentPage >= totalPages - 3 && i >= totalPages - 4) ||
          (i >= currentPage - 1 && i <= currentPage + 1)
        ) {
          pages.push(
            <div
              className={clsx('pagination-num-item', {
                'is-page-active': currentPage === i,
              })}
              key={i}
              onClick={() => handlePageChange(i)}
            >
              {i}
            </div>,
          );
        }
        // 显示省略号
        else if (
          (currentPage <= 4 && i === 6) ||
          (currentPage >= totalPages - 3 && i === totalPages - 5) ||
          (i === currentPage - 2 && currentPage > 4) ||
          (i === currentPage + 2 && currentPage < totalPages - 3)
        ) {
          pages.push(
            <div
              className={clsx('pagination-num-item-more', {
                'is-page-active': currentPage === i,
              })}
              key={i}
              onClick={() => {
                // 点击省略号跳转到附近页码
                if (i < currentPage) {
                  handlePageChange(currentPage - 3);
                } else {
                  handlePageChange(currentPage + 3);
                }
              }}
            >
              ···
            </div>,
          );
        }
      }
    }

    return pages;
  };

  return (
    <div className='pagination-container'>
      {/* 首页 */}
      <div
        className={clsx('pagination-first', {
          'is-disabled': currentPage === 1,
        })}
        onClick={handleFirstPage}
      >
        首页
      </div>

      {/* 上一页 */}
      <div
        className={clsx('pagination-prev', {
          'is-disabled': currentPage === 1,
        })}
        onClick={handlePrevious}
      >
        上一页
      </div>

      {/* 页码数字 */}
      <div className='pagination-numbers'>{renderPageNumbers()}</div>

      {/* 下一页 */}
      <div
        className={clsx('pagination-next', {
          'is-disabled': currentPage === totalPages,
        })}
        onClick={handleNext}
      >
        下一页
      </div>

      {/* 尾页 */}
      <div
        className={clsx('pagination-last', {
          'is-disabled': currentPage === totalPages,
        })}
        onClick={handleLastPage}
      >
        尾页
      </div>
    </div>
  );
};

export default Pagination;
